import { useState, DragEvent, useRef, RefObject, useEffect, useCallback } from "react";
import CardComponent from './CardComponent';
import { Input, Snackbar } from '../../tailwindcomponents';
import { StaffManagementBays, StaffMtpEmployee, StaffManagementAreas } from "../../types/staffManagement";
import { User } from "../../types/user";
import staffApi from "../../api/staffmanagement";
import EmpCard from "./EmpCard";
import React from "react";
import { DragInfo } from "./StaffManagement";

type AlertColor = 'success' | 'info' | 'warning' | 'error';

type Data = {
    bay: StaffManagementBays,
    bayIndex: number,
    isSearchComponent: boolean,
    employees: User[],
    setDragging: Function,
    setLoading: Function,
    isStmAdmin: boolean,
    handleAboutEmployeeDialog: Function,
    attendanceStatus: string[],
    mechanicsSkills: string[],
    dragInfo: RefObject<DragInfo>,
    moveEmployee: (destinationAreaIndex: number, bay: StaffManagementBays) => void,
    tabValue: number,
    setStaffManagementBays: Function
}

const GroupList = ({ dragInfo, moveEmployee, bay, bayIndex, isSearchComponent, employees, setDragging, setLoading, isStmAdmin, handleAboutEmployeeDialog, attendanceStatus, mechanicsSkills, tabValue, setStaffManagementBays }: Data) => {
    const searhText = useRef<string>("");

    // Changes to useState & useEffect to avoid race conditions during bad internet connections
    // If component is mounted and employees are not yet fetched, the array would be set empty and no mechanism to update it - hence an empty seach list
    const [filteredEmployees, setFilteredEmployees] = useState<User[] | undefined>(undefined);
    const [superVisorGroup, setSuperVisorGroup] = useState<StaffManagementAreas | undefined>(undefined);
    const [superVisorGroupIndex, setSuperVisorGroupIndex] = useState<number>(-1);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

    useEffect(() => {
        const area = bay?.areas?.find(area => area.area.includes("SUPERVISOR"));
        if (area) {
            setSuperVisorGroup(area);
            setSuperVisorGroupIndex(bay.areas.findIndex(group => group.area.includes("SUPERVISOR")));
        }
        else{
            setSuperVisorGroup(undefined);
            setSuperVisorGroupIndex(-1);
        }        
        if (filteredEmployees === undefined && employees.length !== 0) {
            setFilteredEmployees(employees);
        }
    }, [employees, tabValue])

    const searchEmployees = (text: string) => {
        searhText.current = text;
        const filteredEmployeesTemp = employees?.filter((employee) => {
            if (attendanceStatus.length === 0 || attendanceStatus.includes(employee.employee_attendance_status)) {
                return employee.name.toLowerCase().includes(text.toLowerCase());
            }
        })
        if (filteredEmployeesTemp !== undefined) {
            setFilteredEmployees(filteredEmployeesTemp);
        }
        if (text.length === 3 || text.length === 2) {
            const employee = employees.filter(s => s.employee_abbreviation?.toUpperCase().trim() === text.toUpperCase());
            if (employee.length > 0) {
                setFilteredEmployees(employee);
            }
        }
    }

    //When the user starts dragging an employee, save the employee info and the bay and group info.
    //! Example of useCallback in GroupList, not strictly necessary unless a group gets really large
    const handleDragStart = useCallback((
        employee: StaffMtpEmployee | User,
        bay: StaffManagementBays | undefined,
        group: StaffManagementAreas | undefined,
        empMappedKey: number,
        groupIndex: number
    ) => {
        //tracking the indexes of the dragged employee and his workstation.
        if (dragInfo && dragInfo.current) {
            dragInfo.current.draggingFromBay = bay;
            dragInfo.current.draggingFromGroup = group;
            dragInfo.current.draggingFromBayIndex = bayIndex;
            dragInfo.current.draggingFromGroupIndex = groupIndex;
            dragInfo.current.draggedEmployeeIndex = empMappedKey;
            dragInfo.current.fromSearchDraggedEmployee = (employee as User).name;
            dragInfo.current.fromSearchDraggedEmployeeNr = (employee as User).Id;
            dragInfo.current.fromSearchDraggedEmployeeStatus = (employee as StaffMtpEmployee).employee_attendance_status;
        }
    }, [bayIndex]);

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    }, []);

    const handleDrop = (e: DragEvent<HTMLDivElement>, bay: StaffManagementBays, group: StaffManagementAreas, dropIngroupIndex: number) => {
        if (isStmAdmin) {
            setDragging(false);
            if (dragInfo?.current?.draggingFromBay?.bay === bay.bay && dragInfo.current.draggingFromGroup?.area === group.area) {//if the dragged employee is from the same bay and group, do nothing.
                return;
            }
            else if (dragInfo?.current?.draggingFromBay?.bay !== bay.bay || dragInfo.current.draggingFromGroup?.area !== group.area) {
                moveEmployee(dropIngroupIndex, bay);
            }
        }
    };

    const deleteEmployeeFromGroup = useCallback(
        (employee: StaffMtpEmployee, group: StaffManagementAreas) => {
            if (!employee || !group) return;
        
            setLoading(true);
            const userName = employee.user_name.split("@")[0];

            staffApi
                .DeleteUserFromGroup(userName, group.area)
                .then(() => {
                    setStaffManagementBays((prev: StaffManagementBays[]) =>
                        prev.map(bay =>
                            bay.bay !== bay.bay
                                ? bay
                                : {
                                    ...bay,
                                    areas: bay.areas.map((area: StaffManagementAreas) =>
                                        area.area !== group.area
                                            ? area
                                            : {
                                                ...area,
                                                employyes_in_group: area.employyes_in_group.filter(
                                                    e => e.user_id !== employee.user_id
                                                )
                                            }
                                    )
                                }
                        )
                    );
                })
                .catch(err => {
                    setSnackbarSeverity('error');
                    setSnackbarMessage(`Could not delete employee: ${err.message}`);
                })
                .finally(() => setLoading(false));
        },
        []
    );

    return (
        <div>
            {isSearchComponent ?
                <div>
                    <div className="fixed top-[75px] z-10 bg-background">
                        <div className="w-[300px] p-1.5 h-[60px]">
                            <Input
                                disabled={false}
                                multiline={true}
                                help_text={""}
                                label_text={"Search By Name or Abbreviation"}
                                value={searhText.current}
                                onTextChange={(text: string) => searchEmployees(text)}
                                type="text"
                                width={100}
                            />
                        </div>
                    </div>
                    <div>
                        {
                            filteredEmployees &&
                            filteredEmployees
                                .filter(el =>
                                (
                                    (attendanceStatus.length === 0 || attendanceStatus.includes(el.employee_attendance_status))
                                    && (mechanicsSkills.length === 0 || mechanicsSkills === undefined || mechanicsSkills.every(value => el.employee_skills.includes(value)))
                                )
                                )
                                .map((item, ix) => <EmpCard key={ix} employee={item} bay={bay} group={undefined} empMappedKey={ix} handleDragStart={handleDragStart} handleDragOver={handleDragOver} handleAboutEmployeeDialog={handleAboutEmployeeDialog} isStmAdmin={isStmAdmin} setDragging={setDragging} deleteEmployeeFromGroup={deleteEmployeeFromGroup} />)
                        }
                    </div>
                </div >
                :
                <>
                    {/* Render Supervisors Group separately */}
                    {superVisorGroup !== undefined &&
                        <div
                            className="w-[55%] mx-auto p-4 text-center"
                            key={`supervisor-${superVisorGroupIndex}`}
                            onDragOver={handleDragOver}
                            onDrop={e => handleDrop(e, bay, superVisorGroup, superVisorGroupIndex)}
                        >
                            <CardComponent banner_color="#266E43" bay={`${superVisorGroup.area}`} key={`supervisor-${superVisorGroupIndex}`}>
                                <div>
                                    {superVisorGroup.employyes_in_group
                                        .filter(el =>
                                        (
                                            (attendanceStatus.length === 0 || attendanceStatus.includes(el.employee_attendance_status)) &&
                                            (mechanicsSkills.length === 0 || mechanicsSkills === undefined || mechanicsSkills.every(value => el.employee_skills.includes(value)))
                                        )
                                        )
                                        .map((item: StaffMtpEmployee, ix) => (
                                            <EmpCard
                                                key={`supervisor-emp-${ix}`}
                                                employee={item}
                                                bay={bay}
                                                group={superVisorGroup}
                                                empMappedKey={ix}
                                                handleDragStart={handleDragStart}
                                                handleDragOver={handleDragOver}
                                                handleAboutEmployeeDialog={handleAboutEmployeeDialog}
                                                isStmAdmin={isStmAdmin}
                                                setDragging={setDragging}
                                                deleteEmployeeFromGroup={deleteEmployeeFromGroup}
                                            />
                                        ))}
                                </div>
                            </CardComponent>
                        </div>
                    }
                    <div className="columns-2 gap-4" >
                        {/* Render deparment groups */}
                        {bay !== undefined && bay.areas.map((group, idx) => {
                            if (group.area.includes("SUPERVISOR")) {
                                return null;
                            }
                            return (
                                <div className="inline-block mb-4 w-full" key={idx} onDragOver={handleDragOver} onDrop={e => handleDrop(e, bay, group, idx)}>
                                    <CardComponent banner_color="#266E43" bay={`${group.area}`} key={idx}>
                                        <div>
                                            {group.employyes_in_group
                                                .filter(el =>
                                                (
                                                    (attendanceStatus.length === 0 || attendanceStatus.includes(el.employee_attendance_status)) &&
                                                    (mechanicsSkills.length === 0 || mechanicsSkills === undefined || mechanicsSkills.every(value => el.employee_skills.includes(value)))
                                                )
                                                )
                                                .map((item: StaffMtpEmployee, ix) => (
                                                    <EmpCard
                                                        key={ix}
                                                        employee={item}
                                                        bay={bay}
                                                        group={group}
                                                        empMappedKey={ix}
                                                        handleDragStart={handleDragStart}
                                                        handleDragOver={handleDragOver}
                                                        handleAboutEmployeeDialog={handleAboutEmployeeDialog}
                                                        isStmAdmin={isStmAdmin}
                                                        setDragging={setDragging}
                                                        deleteEmployeeFromGroup={deleteEmployeeFromGroup}
                                                    />
                                                ))}
                                        </div>
                                    </CardComponent>
                                </div>
                            );
                        })}
                    </div>
                </>
            }
            <Snackbar
                onSnackBarClose={() => setSnackbarMessage("")}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={true}
                horizontalPosition='right'
                severity={snackbarSeverity}
            >
            </Snackbar>
        </div >
    )
}
export default React.memo(GroupList);